import {useCallback, useEffect, useRef, type RefObject} from 'react'

/**
 * Closes a list or menu on a press outside it (STFFLG-4236).
 *
 * A press is inside when it lands in one of `boxes`, or anywhere in the owner's React tree: give
 * the returned handler to `onMouseDown` on the element that holds the rows. The React tree is what
 * counts for a portaled piece of a row, such as its DS Tooltip bubble: that sits in `<body>`,
 * outside every box, so a press on it would otherwise close the list with nothing chosen. The DS
 * `Popover` reads presses the same way.
 *
 * The capture-phase reset runs before React's handlers, so an event whose propagation something
 * stopped can't leave the inside flag set.
 */
export function useOutsidePress(
  active: boolean,
  boxes: ReadonlyArray<RefObject<Element>>,
  onOutside: () => void,
): () => void {
  const pressedInsideRef = useRef(false)
  const boxesRef = useRef(boxes)
  boxesRef.current = boxes
  const onOutsideRef = useRef(onOutside)
  onOutsideRef.current = onOutside

  useEffect(() => {
    if (!active) return
    const reset = () => {
      pressedInsideRef.current = false
    }
    const onMouseDown = (event: MouseEvent) => {
      const pressedInside = pressedInsideRef.current
      pressedInsideRef.current = false
      if (pressedInside) return
      const target = event.target
      if (target instanceof Node && boxesRef.current.some((box) => box.current?.contains(target)))
        return
      onOutsideRef.current()
    }
    document.addEventListener('mousedown', reset, true)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('mousedown', reset, true)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [active])

  return useCallback(() => {
    pressedInsideRef.current = true
  }, [])
}
